import { useEffect, useRef, useState } from 'react'

type UseCellHoverEventsResult = {
  isHovered: boolean
  isRowHovered: boolean
  setIsRowHovered: React.Dispatch<React.SetStateAction<boolean>>
  chartContainerRef: React.RefObject<HTMLDivElement>
  /**
   * Called on cell leave, after isHovered/isRowHovered are reset.
   * Use to reset additional state (e.g. CP row hover).
   */
  onCellLeaveExtraRef: React.MutableRefObject<() => void>
  /**
   * Called on cell-level mousemove with pre-computed containerRect.
   * Use for critical path pre-drag tracking.
   */
  onCellMouseMoveRef: React.MutableRefObject<
    (e: MouseEvent, containerRect: DOMRect) => void
  >
  /**
   * Called on cell-level mousedown with pre-computed containerRect.
   * Use for critical path drag start via indicator icon.
   */
  onCellMouseDownRef: React.MutableRefObject<
    (e: MouseEvent, containerRect: DOMRect) => void
  >
}

/**
 * Manages cell-level hover state and event delegation for GanttChart.
 *
 * Attaches mouseenter/mouseleave/mousemove/mousedown listeners to the parent
 * ag-grid cell element (closest '[col-id]'). This avoids hover events being
 * blocked by label elements that overlay the chart.
 *
 * Returns mutable callback refs that the caller updates each render to wire up
 * handlers from dep/cp hooks (avoiding circular dependency with isHovered).
 */
export const useCellHoverEvents = (): UseCellHoverEventsResult => {
  const [isHovered, setIsHovered] = useState(false)
  const [isRowHovered, setIsRowHovered] = useState(false)

  const chartContainerRef = useRef<HTMLDivElement>(null)
  const onCellLeaveExtraRef = useRef<() => void>(() => {
    return
  })
  const onCellMouseMoveRef = useRef<
    (e: MouseEvent, containerRect: DOMRect) => void
  >(() => {
    return
  })
  const onCellMouseDownRef = useRef<
    (e: MouseEvent, containerRect: DOMRect) => void
  >(() => {
    return
  })

  useEffect(() => {
    const el = chartContainerRef.current
    if (!el) return
    const cell: HTMLElement | null = el.closest('[col-id]')
    if (!cell) return
    const onEnter = () => setIsHovered(true)
    const onLeave = () => {
      setIsHovered(false)
      setIsRowHovered(false)
      onCellLeaveExtraRef.current()
    }
    const onMouseMove = (e: MouseEvent) => {
      const container = chartContainerRef.current
      if (!container) return
      onCellMouseMoveRef.current(e, container.getBoundingClientRect())
    }
    const onMouseDown = (e: MouseEvent) => {
      const container = chartContainerRef.current
      if (!container) return
      onCellMouseDownRef.current(e, container.getBoundingClientRect())
    }
    cell.addEventListener('mouseenter', onEnter)
    cell.addEventListener('mouseleave', onLeave)
    cell.addEventListener('mousemove', onMouseMove)
    cell.addEventListener('mousedown', onMouseDown)
    return () => {
      cell.removeEventListener('mouseenter', onEnter)
      cell.removeEventListener('mouseleave', onLeave)
      cell.removeEventListener('mousemove', onMouseMove)
      cell.removeEventListener('mousedown', onMouseDown)
    }
  }, [])

  return {
    isHovered,
    isRowHovered,
    setIsRowHovered,
    chartContainerRef,
    onCellLeaveExtraRef,
    onCellMouseMoveRef,
    onCellMouseDownRef,
  }
}
